import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Checkbox, Menu, MenuItem, Snackbar, Stack } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FullscreenExitOutlinedIcon from '@mui/icons-material/FullscreenExitOutlined';
import FullscreenOutlinedIcon from '@mui/icons-material/FullscreenOutlined';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';

import logo from '../../../livezone_logo.png?inline';
import { useEventContext } from 'providers/EventProvider';
import { useSplitContext } from 'providers/SplitProvider';

const barButtonSx = {
  height: 32,
  borderRadius: '10px',
  border: '1px solid #ececef',
  color: '#2c2c2c',
  fontWeight: 600,
  fontSize: 14,
  px: 1.5,
  bgcolor: '#fff',
  whiteSpace: 'nowrap',
  '&:hover': { bgcolor: '#fafafa', borderColor: '#e0e0e4' }
};

const formatClock = (time: number) => {
  const totalSeconds = Math.max(0, Math.floor(time / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => (value < 10 ? `0${value}` : `${value}`);
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(minutes)}:${pad(seconds)}`;
};

type EventHeaderProps = {
  shellRef: React.RefObject<HTMLDivElement | null>;
  hiddenRouteIds: number[];
  setHiddenRouteIds: React.Dispatch<React.SetStateAction<number[]>>;
};

const EventHeader = ({ shellRef, hiddenRouteIds, setHiddenRouteIds }: EventHeaderProps) => {
  const navigate = useNavigate();
  const { event } = useEventContext();
  const { time } = useSplitContext();
  const [courseMenuEl, setCourseMenuEl] = React.useState<HTMLElement | null>(null);
  const [eventMenuEl, setEventMenuEl] = React.useState<HTMLElement | null>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  const subEvents = event.subEvents || [];
  const hasMultipleSubEvents = subEvents.length > 1;
  const routes = event.routes || [];
  const visibleCount = routes.length - hiddenRouteIds.length;
  const competitorCount = event.competitors.length;

  React.useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleRoute = (routeId: number) => {
    setHiddenRouteIds((current) => {
      const hidden = new Set(current);
      if (hidden.has(routeId)) {
        hidden.delete(routeId);
      } else {
        hidden.add(routeId);
      }
      return [...hidden];
    });
  };

  const coursesLabel = () => {
    if (hiddenRouteIds.length === 0) return 'All courses';
    if (visibleCount === 1) {
      return routes.find((route) => !hiddenRouteIds.includes(route.id))?.name ?? '1 course';
    }
    if (visibleCount <= 0) return 'No courses';
    return `${visibleCount} courses`;
  };

  const onShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: event.name, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setNotice('Link copied');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setNotice('Could not share this event');
    }
  };

  const onFullscreen = async () => {
    const shell = shellRef.current;
    if (!shell) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await shell.requestFullscreen();
      }
    } catch {
      setNotice('Fullscreen is not available');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        px: { xs: 1.5, md: 2 },
        py: 0.75,
        borderBottom: '1px solid #f2f2f4',
        minHeight: 48
      }}
    >
      <Box component="img" src={logo} alt="LiveZone" sx={{ height: 22, width: 'auto', display: 'block' }} />

      {hasMultipleSubEvents ? (
        <>
          <Button
            aria-haspopup="menu"
            aria-expanded={Boolean(eventMenuEl)}
            onClick={(eventClick) => setEventMenuEl(eventClick.currentTarget)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: eventMenuEl ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s'
                }}
              />
            }
            sx={{
              color: '#161616',
              fontWeight: 700,
              fontSize: 15,
              px: 1,
              height: 32,
              minWidth: 0,
              maxWidth: { xs: 180, sm: 280 },
              '& .MuiButton-endIcon': { ml: 0.25 }
            }}
          >
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {event.name}
            </Box>
          </Button>
          <Menu
            anchorEl={eventMenuEl}
            open={Boolean(eventMenuEl)}
            onClose={() => setEventMenuEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {subEvents.map((subEvent) => (
              <MenuItem
                key={subEvent.sub}
                selected={subEvent.sub === event.sub}
                onClick={() => {
                  setEventMenuEl(null);
                  if (subEvent.sub !== event.sub) {
                    navigate(`/${event.slug}/${subEvent.sub}`);
                  }
                }}
              >
                {subEvent.name}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : (
        <Box
          sx={{
            color: '#161616',
            fontWeight: 700,
            fontSize: 15,
            px: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: { xs: 180, sm: 280 }
          }}
        >
          {event.name}
        </Box>
      )}

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ color: '#6b7280', fontWeight: 600, fontSize: 14, order: { xs: 3, md: 0 } }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />
          <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatClock(time)}
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <PeopleAltOutlinedIcon sx={{ fontSize: 18 }} />
          <Box component="span">
            {competitorCount}
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {' '}
              {competitorCount === 1 ? 'competitor' : 'competitors'}
            </Box>
          </Box>
        </Stack>
      </Stack>

      <Box sx={{ flex: '1 1 12px', order: { xs: 2, md: 0 } }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', order: { xs: 4, md: 0 } }}>
        {routes.length > 0 && (
          <>
            <Button
              onClick={(clickEvent) => setCourseMenuEl(clickEvent.currentTarget)}
              startIcon={<LayersOutlinedIcon sx={{ fontSize: 18 }} />}
              endIcon={<ExpandMoreIcon />}
              sx={barButtonSx}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {coursesLabel()}
              </Box>
            </Button>
            <Menu anchorEl={courseMenuEl} open={Boolean(courseMenuEl)} onClose={() => setCourseMenuEl(null)}>
              <MenuItem onClick={() => setHiddenRouteIds([])}>
                <Checkbox size="small" checked={hiddenRouteIds.length === 0} sx={{ p: 0.5, mr: 1 }} />
                All courses
              </MenuItem>
              {routes.map((route) => (
                <MenuItem key={route.id} onClick={() => toggleRoute(route.id)}>
                  <Checkbox size="small" checked={!hiddenRouteIds.includes(route.id)} sx={{ p: 0.5, mr: 1 }} />
                  {route.name}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        <Button onClick={onShare} startIcon={<IosShareOutlinedIcon sx={{ fontSize: 18 }} />} sx={barButtonSx}>
          <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
            Share
          </Box>
        </Button>

        <Button
          onClick={onFullscreen}
          startIcon={
            isFullscreen ? <FullscreenExitOutlinedIcon sx={{ fontSize: 18 }} /> : <FullscreenOutlinedIcon sx={{ fontSize: 18 }} />
          }
          sx={barButtonSx}
        >
          <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
            {isFullscreen ? 'Exit' : 'Fullscreen'}
          </Box>
        </Button>
      </Box>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={2500}
        onClose={() => setNotice(null)}
        message={notice}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default EventHeader;
